import { Link } from 'react-router-dom';
import { relativeTime } from '../../lib/format.ts';
import type { CoverWarm } from '../../lib/coverWarm.ts';
import type { DeckSummary } from '../../../shared/deck.ts';

/**
 * "Jump back in" — a row of the decks you were last in, above whichever body
 * is showing.
 *
 * Read-only navigation, and only that. It carries none of the deck card's
 * machinery — no kebab, no drag-to-file, no select checkbox — because every one
 * of those is a way to CHANGE a deck, and a shortcut row that can rename or bin
 * something is a row you have to be careful in. One target per entry, going to
 * the same place the grid card's own link goes.
 *
 * Which decks it holds, and when it is suppressed entirely, is
 * `recentStrip.ts`; this file only draws the answer.
 */

type RecentStripProps = {
  /** Already picked, ordered and capped by `recentStripDecks`. Empty renders
   *  nothing at all. */
  decks: DeckSummary[];
  /** The dashboard's cover cache, slug → URL. */
  covers: Record<string, string | null>;
  /** Read for `warmingNow` only, so a placeholder shimmers when something is
   *  genuinely coming.
   *
   *  The strip deliberately does NOT attach `coverRef`: the warm queue's
   *  observer maps element → slug, and detaching one of two elements sharing a
   *  slug unregisters both, which would quietly stop the grid card underneath
   *  from ever asking for its cover. The strip shows whatever the grid has
   *  already fetched and a plain panel otherwise. */
  warm: CoverWarm;
};

export default function RecentStrip({ decks, covers, warm }: RecentStripProps) {
  if (!decks.length) return null;
  return (
    <section className="recent-strip" aria-labelledby="recent-strip-title">
      <h2 className="recent-strip__title" id="recent-strip-title">
        Jump back in
        {/* Whose history this is, per frame noJYP. Faint and beside the
            heading rather than under it: it qualifies the row, it is not a
            second thing to read. */}
        <span className="recent-strip__note">· last opened by you</span>
      </h2>
      {/* Scrolls sideways rather than wrapping, at every width: a shortcut row
          that becomes two rows on a narrow window has stopped being a row. The
          scroll lives on this list, never on the page. */}
      <ul className="recent-strip__list" role="list">
        {decks.map((d) => {
          const cover = covers[d.slug] || null;
          return (
            <li key={d.slug} className="recent-strip__item">
              <Link to={`/editor/${d.slug}`} className="recent-card" title={`${d.title} (${d.slug})`}>
                {/* aria-hidden: the title sits right beside it and names the
                    deck, and there is nothing focusable in here. */}
                <span className="recent-card__cover" aria-hidden="true">
                  {cover ? (
                    // draggable={false} for the same reason the deck card's
                    // image carries it: a native image drag would hijack the
                    // gesture with a payload nothing on this page reads.
                    <img
                      className="recent-card__cover-img"
                      src={cover}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  ) : (
                    <span className={'recent-card__cover-ph' + (warm.warmingNow(d.slug) ? ' is-working' : '')} />
                  )}
                </span>
                <span className="recent-card__text">
                  <span className="recent-card__title">{d.title}</span>
                  <span className="recent-card__meta">opened {relativeTime(d.lastOpenedAt)}</span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
